import React, { useMemo, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Text, Group, Checkbox } from '@mantine/core';

/**
 * The conversion graph for an "All <type>" selection.
 *
 * The page keeps its three graphs and its Conversions / Conversion % toggle; this
 * draws whichever one is selected ONCE PER GOAL instead of once for the test.
 * A single shared axis cannot work here: a test can carry a dozen goals whose
 * conversion rates differ by an order of magnitude, so the small goals would be
 * flattened to nothing. Faceting gives each goal its own y-scale.
 *
 * Colour does no encoding work — the variation is identified by its position and
 * label — so the panels use one accent for the leading variation and a recessive
 * grey for the rest, rather than a dozen categorical hues nobody can tell apart.
 *
 * Only the leading bar is labelled, so every other value is read by hovering.
 * The hover target is the full column height (or a wide invisible stroke over a
 * line), never the drawn mark itself, which for a low-converting goal can be two
 * pixels tall.
 *
 * @param breakdown       goal_breakdown       [variation => goal rows]
 * @param breakdownChart  goal_breakdown_chart [goal_id => {dates, series}]
 * @param graph           '0' main | '1' cumulative | '2' avg revenue per unique
 * @param metric          '1' conversions | '2' conversion %
 */

const ACCENT_LIGHT = '#087f5b';
const ACCENT_DARK  = '#12b886';
const MUTED_LIGHT  = '#868e96';
const MUTED_DARK   = '#7c8085';

type GoalSeries = {
  goalId: string;
  name: string;
  convs: number[];
  rates: number[];
  epcs: number[];
  revenues: string[];
  total: number;
};

type Tip = { x: number; y: number; title: string; lines: string[] } | null;

export function GoalFacetCharts({
  breakdown,
  breakdownChart,
  goals,
  graph = '0',
  metric = '1',
  isDark = false,
  showEmpty,
  onToggleEmpty,
}: any) {
  const accent = isDark ? ACCENT_DARK : ACCENT_LIGHT;
  const muted  = isDark ? MUTED_DARK : MUTED_LIGHT;
  const grid   = isDark ? '#2c2e33' : '#e9ecef';
  const panelBorder = isDark ? '#2c2e33' : '#e9ecef';

  const [tip, setTip] = useState<Tip>(null);

  const showTip = useCallback((e: React.MouseEvent, title: string, lines: string[]) => {
    setTip({ x: e.clientX, y: e.clientY, title, lines });
  }, []);
  const hideTip = useCallback(() => setTip(null), []);

  /* Pivot goal_breakdown (keyed by variation) into one series per goal. Every
     measure is kept so a tooltip can show the full picture whichever graph is on. */
  const series: GoalSeries[] = useMemo(() => {
    const variations = Object.keys(breakdown || {})
      .map((v) => Number(v))
      .filter((v) => !isNaN(v))
      .sort((a, b) => a - b);
    if (!variations.length) return [];

    const byGoal: Record<string, GoalSeries> = {};
    variations.forEach((v) => {
      (breakdown[String(v)] || []).forEach((row: any) => {
        const id = row.goal_id;
        if (!byGoal[id]) {
          byGoal[id] = { goalId: id, name: row.goal_name, convs: [], rates: [], epcs: [], revenues: [], total: 0 };
        }
        const conv = Number(row.pageconvnum) || 0;
        byGoal[id].convs.push(conv);
        byGoal[id].rates.push(parseFloat(row.pageconv) || 0);
        byGoal[id].epcs.push(parseFloat(row.avg_revenue_per_unique) || 0);
        byGoal[id].revenues.push(row.revenue ?? '0.00');
        byGoal[id].total += conv;
      });
    });
    const order = Array.isArray(goals) && goals.length
      ? goals.map((g: any) => g.goal_id).filter((id: string) => byGoal[id])
      : Object.keys(byGoal);
    return order.map((id: string) => byGoal[id]);
  }, [breakdown, goals]);

  const variationNumbers = useMemo(
    () => Object.keys(breakdown || {}).map((v) => Number(v)).filter((v) => !isNaN(v)).sort((a, b) => a - b),
    [breakdown]
  );

  if (!series.length) {
    return <Text size="sm" color="dimmed">No goal data to chart yet.</Text>;
  }

  const emptyCount = series.filter((s) => s.total === 0).length;
  const shown = showEmpty ? series : series.filter((s) => s.total > 0);

  if (!shown.length) {
    return <Text size="sm" color="dimmed">None of these goals has recorded a conversion yet.</Text>;
  }

  const plotValue = (s: GoalSeries, i: number) =>
    graph === '2' ? s.epcs[i] : (metric === '2' ? s.rates[i] : s.convs[i]);

  const plotLabel = (s: GoalSeries, i: number) =>
    graph === '2' ? s.epcs[i].toFixed(2)
      : metric === '2' ? s.rates[i].toFixed(2) + ' %'
      : String(s.convs[i]);

  /* One tooltip body for every graph, so the reader always gets the same facts. */
  const tipLines = (s: GoalSeries, i: number) => {
    const lines = [
      `${s.convs[i]} conversion${s.convs[i] === 1 ? '' : 's'} · ${s.rates[i].toFixed(2)} %`,
    ];
    if (graph === '2' || parseFloat(s.revenues[i]) > 0) {
      lines.push(`Revenue ${s.revenues[i]} · EPC ${s.epcs[i].toFixed(2)}`);
    }
    return lines;
  };

  const tipTitle = (s: GoalSeries, i: number) =>
    `${s.name} — Variation #${variationNumbers[i]}${variationNumbers[i] === 1 ? ' (Control)' : ''}`;

  return (
    <div style={{ width: '100%', position: 'relative' }}>
      <Group position="apart" mb={10} spacing={12}>
        <Group spacing={8}>
          <span style={{ width: 10, height: 10, borderRadius: 2, background: accent, display: 'inline-block' }} />
          <Text size="xs" color="dimmed">best variation for that goal</Text>
        </Group>
        {emptyCount > 0 && (
          <Checkbox
            size="xs"
            checked={!!showEmpty}
            onChange={(e) => onToggleEmpty && onToggleEmpty(e.currentTarget.checked)}
            label={`Show ${emptyCount} goal${emptyCount === 1 ? '' : 's'} with no conversions`}
          />
        )}
      </Group>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 16 }}>
        {shown.map((s) => {
          // the leader is decided on conversion RATE, matching how the table ranks
          let best = 0;
          s.rates.forEach((r, i) => { if (r > s.rates[best]) best = i; });

          if (s.total === 0) {
            return (
              <div key={s.goalId} style={{ border: `1px solid ${panelBorder}`, borderRadius: 5, padding: '10px 11px 8px', opacity: 0.6 }}>
                <Text size="xs" weight={600}>{s.name}</Text>
                <Text size={11} color="dimmed" mb={8}>no conversions</Text>
                <div style={{ height: 74, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Text size={11} color="dimmed">—</Text>
                </div>
              </div>
            );
          }

          const chart = breakdownChart && breakdownChart[s.goalId];
          const isCumulative = graph === '1' && chart && Array.isArray(chart.dates) && chart.dates.length > 0;

          return (
            <div key={s.goalId} style={{ border: `1px solid ${panelBorder}`, borderRadius: 5, padding: '10px 11px 8px' }}>
              <Text size="xs" weight={600} style={{ textWrap: 'balance' } as any}>{s.name}</Text>
              <Text size={11} color="dimmed" mb={8} style={{ fontVariantNumeric: 'tabular-nums' }}>
                {s.total} conversions · best #{variationNumbers[best]}
              </Text>

              {isCumulative ? (
                <CumulativeFacet
                  chart={chart}
                  best={best}
                  accent={accent}
                  muted={muted}
                  grid={grid}
                  variationNumbers={variationNumbers}
                  onHover={(e: React.MouseEvent, idx: number, total: number) =>
                    showTip(e, tipTitle(s, idx), [
                      `${total} conversion${total === 1 ? '' : 's'} to date`,
                      `${s.rates[idx].toFixed(2)} % conversion rate`,
                    ])}
                  onLeave={hideTip}
                />
              ) : (
                <ColumnFacet
                  values={s.convs.map((_, i) => plotValue(s, i))}
                  label={plotLabel(s, best)}
                  best={best}
                  labels={variationNumbers}
                  accent={accent}
                  muted={muted}
                  grid={grid}
                  onHover={(e: React.MouseEvent, i: number) => showTip(e, tipTitle(s, i), tipLines(s, i))}
                  onLeave={hideTip}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Portalled to <body> on purpose. This chart lives inside a Mantine Card,
          which sets overflow:hidden — a transformed ancestor would then become the
          containing block for a position:fixed child and clip the tooltip away.
          The page already portals its Select and Popover for the same reason. */}
      {tip && typeof document !== 'undefined'
        && createPortal(<ChartTooltip tip={tip} isDark={isDark} />, document.body)}
    </div>
  );
}

function ChartTooltip({ tip, isDark }: any) {
  const pad = 14;
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1200;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
  const style: React.CSSProperties = {
    position: 'fixed',
    left: Math.max(8, Math.min(tip.x + pad, vw - 268)),
    top: tip.y + pad > vh - 90 ? Math.max(8, tip.y - 78) : tip.y + pad,
    zIndex: 500,
    pointerEvents: 'none',
    background: isDark ? '#25262b' : '#ffffff',
    color: isDark ? '#e9ecef' : '#212529',
    border: `1px solid ${isDark ? '#3a3d42' : '#dee2e6'}`,
    borderRadius: 4,
    padding: '7px 10px',
    fontSize: 11.5,
    lineHeight: 1.5,
    maxWidth: 250,
    boxShadow: '0 3px 12px rgba(0,0,0,.16)',
    fontVariantNumeric: 'tabular-nums',
  };
  return (
    <div style={style} role="tooltip">
      <div style={{ fontWeight: 600, marginBottom: 2 }}>{tip.title}</div>
      {tip.lines.map((l: string, i: number) => (
        <div key={i} style={{ opacity: i === 0 ? 1 : 0.75 }}>{l}</div>
      ))}
    </div>
  );
}

function ColumnFacet({ values, label, best, labels, accent, muted, grid, onHover, onLeave }: any) {
  const max = Math.max(...values, 0);
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 74, borderBottom: `1px solid ${grid}` }}>
        {values.map((v: number, i: number) => (
          /* the whole column is the hit target, not the drawn bar, which can be
             two pixels tall on a low-converting goal */
          <div
            key={i}
            onMouseEnter={(e) => onHover(e, i)}
            onMouseMove={(e) => onHover(e, i)}
            onMouseLeave={onLeave}
            style={{
              flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
              height: '100%', position: 'relative', cursor: 'default',
            }}
          >
            {i === best && (
              <Text
                size={10}
                weight={700}
                style={{ position: 'absolute', top: -13, left: '50%', transform: 'translateX(-50%)', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}
              >
                {label}
              </Text>
            )}
            <div style={{
              height: max > 0 ? Math.max(Math.round((v / max) * 66), 2) : 2,
              background: i === best ? accent : muted,
              borderRadius: '3px 3px 0 0',
            }} />
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 2, marginTop: 4 }}>
        {labels.map((n: number) => (
          <span key={n} style={{ flex: 1, textAlign: 'center', fontSize: 9.5, color: '#adb5bd', fontVariantNumeric: 'tabular-nums' }}>{n}</span>
        ))}
      </div>
    </>
  );
}

function CumulativeFacet({ chart, best, accent, muted, grid, variationNumbers, onHover, onLeave }: any) {
  const W = 200, H = 66;
  const variations = Object.keys(chart.series).map((v) => Number(v)).sort((a, b) => a - b);
  let max = 0;
  variations.forEach((v) => (chart.series[v] || []).forEach((y: number) => { if (y > max) max = y; }));
  if (max <= 0) max = 1;
  const days = chart.dates.length;

  const pointsFor = (v: number) =>
    (chart.series[v] || [])
      .map((y: number, d: number) => `${(days > 1 ? (d / (days - 1)) * W : 0).toFixed(1)},${(H - (y / max) * H).toFixed(1)}`)
      .join(' ');

  return (
    <>
      <div style={{ height: 74, display: 'flex', alignItems: 'stretch' }}>
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width: '100%', height: 74, overflow: 'visible' }} role="img" aria-label="Cumulative conversions">
          <line x1="0" y1={H} x2={W} y2={H} stroke={grid} strokeWidth="1" />
          {variations.map((v, idx) => {
            const isBest = idx === best;
            return (
              <polyline
                key={v}
                fill="none"
                stroke={isBest ? accent : muted}
                strokeWidth={isBest ? 2 : 1.25}
                strokeLinejoin="round"
                strokeLinecap="round"
                opacity={isBest ? 1 : 0.5}
                points={pointsFor(v)}
                vectorEffect="non-scaling-stroke"
              />
            );
          })}
          {/* invisible fat strokes on top, so a 1px line is still easy to hover */}
          {variations.map((v, idx) => {
            const ser = chart.series[v] || [];
            const total = ser.length ? ser[ser.length - 1] : 0;
            return (
              <polyline
                key={`hit-${v}`}
                fill="none"
                stroke="transparent"
                strokeWidth={12}
                points={pointsFor(v)}
                vectorEffect="non-scaling-stroke"
                style={{ cursor: 'default' }}
                onMouseEnter={(e) => onHover(e, idx, total)}
                onMouseMove={(e) => onHover(e, idx, total)}
                onMouseLeave={onLeave}
              />
            );
          })}
        </svg>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 9.5, color: '#adb5bd' }}>
        <span>{chart.dates[0]}</span>
        <span>{chart.dates[chart.dates.length - 1]}</span>
      </div>
    </>
  );
}
